import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { authFetch } from '../api';
import {
  Plus,
  Search,
  Filter,
  Download,
  Trash2,
  Edit,
  Eye,
  FileText,
  Settings,
  Calendar,
  DollarSign,
  User,
  Building,
  FileUp,
  X,
  CheckCircle,
  AlertCircle,
  Clock,
  FolderOpen
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Contracts = () => {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('contracts');
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [settings, setSettings] = useState({});
  const [customStoragePath, setCustomStoragePath] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState('');
  const [updatingStatuses, setUpdatingStatuses] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    clientName: '',
    contractType: 'Service',
    startDate: '',
    endDate: '',
    contractValue: '',
    description: '',
    assignedProject: '',
    responsiblePerson: ''
  });

  // File upload states
  const [files, setFiles] = useState([]);
  const [fileCategory, setFileCategory] = useState('Other');

  const { t } = useTranslation();

  useEffect(() => {
    fetchContracts();
    fetchSettings();
  }, []);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const response = await authFetch('/contracts');
      setContracts(response);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await authFetch('/contracts/settings');
      setSettings(response);
      setCustomStoragePath(response.uploadDirectory || '');
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const updateSettings = async () => {
    try {
      setSavingSettings(true);
      setSettingsMessage('');
      
      const response = await authFetch('/contracts/settings', {
        method: 'PUT',
        body: { uploadDirectory: customStoragePath }
      });
      
      setSettingsMessage(t('contracts.storageDirectoryUpdated'));
      setSettings(prev => ({ ...prev, uploadDirectory: customStoragePath }));
      
      // Clear message after 3 seconds
      setTimeout(() => setSettingsMessage(''), 3000);
    } catch (error) {
      console.error('Error updating settings:', error);
      setSettingsMessage(t('contracts.errorUpdatingStorageDirectory'));
      
      // Clear error message after 5 seconds
      setTimeout(() => setSettingsMessage(''), 5000);
    } finally {
      setSavingSettings(false);
    }
  };

  const resetToDefault = async () => {
    try {
      setSavingSettings(true);
      setSettingsMessage('');
      
      // Get the default path from backend
      const response = await authFetch('/contracts/settings');
      const defaultPath = response.uploadDirectory;
      
      // Update to default path
      await authFetch('/contracts/settings', {
        method: 'PUT',
        body: { uploadDirectory: defaultPath }
      });
      
      setCustomStoragePath(defaultPath);
      setSettingsMessage(t('contracts.resetToDefaultDirectory'));
      
      // Clear message after 3 seconds
      setTimeout(() => setSettingsMessage(''), 3000);
    } catch (error) {
      console.error('Error resetting to default:', error);
      setSettingsMessage(t('contracts.errorResettingToDefault'));
      
      // Clear error message after 5 seconds
      setTimeout(() => setSettingsMessage(''), 5000);
    } finally {
      setSavingSettings(false);
    }
  };

  const updateAllContractStatuses = async () => {
    try {
      setUpdatingStatuses(true);
      setSettingsMessage('');
      
      const response = await authFetch('/contracts/update-statuses', {
        method: 'POST'
      });
      
      setSettingsMessage(t('contracts.successfullyUpdatedContractStatuses', { count: response.updatedCount }));
      
      // Refresh contracts list to show updated statuses
      fetchContracts();
      
      // Clear message after 5 seconds
      setTimeout(() => setSettingsMessage(''), 5000);
    } catch (error) {
      console.error('Error updating contract statuses:', error);
      setSettingsMessage(t('contracts.errorUpdatingContractStatuses'));
      
      // Clear error message after 5 seconds
      setTimeout(() => setSettingsMessage(''), 5000);
    } finally {
      setUpdatingStatuses(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (showEditModal && selectedContract) {
        await authFetch(`/contracts/${selectedContract._id}`, {
          method: 'PUT',
          body: formData
        });
      } else {
        await authFetch('/contracts', {
          method: 'POST',
          body: formData
        });
      }
      
      fetchContracts();
      resetForm();
      setShowAddModal(false);
      setShowEditModal(false);
    } catch (error) {
      console.error('Error saving contract:', error);
      alert(t('contracts.errorSavingContract'));
    }
  };

  const handleFileUpload = async () => {
    if (!selectedContract || files.length === 0) return;

    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      formData.append('category', fileCategory);

      // Get the JWT token for authentication
      const token = localStorage.getItem('jwtToken');
      
      // Make direct fetch request for file upload (FormData doesn't work well with authFetch)
      const response = await fetch(`/api/contracts/${selectedContract._id}/files`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('contracts.fileUploadFailed'));
      }

      const result = await response.json();
      console.log('File upload successful:', result);

      fetchContracts();
      setFiles([]);
      setFileCategory('Other');
      setShowFileModal(false);
    } catch (error) {
      console.error('Error uploading files:', error);
      alert(t('contracts.errorUploadingFiles', { error: error.message }));
    }
  };

  const handleDeleteContract = async (contractId) => {
    if (!confirm(t('contracts.confirmDeleteContract'))) return;

    try {
      await authFetch(`/contracts/${contractId}`, {
        method: 'DELETE'
      });
      fetchContracts();
    } catch (error) {
      console.error('Error deleting contract:', error);
      alert(t('contracts.errorDeletingContract'));
    }
  };

  const handleDeleteFile = async (contractId, fileId) => {
    if (!confirm(t('contracts.confirmDeleteFile'))) return;

    try {
      await authFetch(`/contracts/${contractId}/files/${fileId}`, {
        method: 'DELETE'
      });
      fetchContracts();
    } catch (error) {
      console.error('Error deleting file:', error);
      alert(t('contracts.errorDeletingFile'));
    }
  };

  const openFileLocation = async (contractId, fileId) => {
    try {
      // Get the contract to find the file details
      const contract = contracts.find(c => c._id === contractId);
      if (!contract) {
        alert(t('contracts.contractNotFound'));
        return;
      }

      const file = contract.files.find(f => f._id === fileId);
      if (!file) {
        alert(t('contracts.fileNotFound'));
        return;
      }

      // Make a request to the backend to open the file location
      const response = await authFetch(`/contracts/${contractId}/files/${fileId}/open-location`, {
        method: 'POST'
      });

      if (response.success) {
        console.log('File location opened successfully');
      } else {
        alert(t('contracts.couldNotOpenFileLocation'));
      }
    } catch (error) {
      console.error('Error opening file location:', error);
              alert(t('contracts.errorOpeningFileLocation'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      clientName: '',
      contractType: 'Service',
      startDate: '',
      endDate: '',
      contractValue: '',
      description: '',
      assignedProject: '',
      responsiblePerson: ''
    });
    setSelectedContract(null);
  };

  const openEditModal = (contract) => {
    setSelectedContract(contract);
    setFormData({
      name: contract.name,
      clientName: contract.clientName,
      contractType: contract.contractType,
      startDate: contract.startDate.split('T')[0],
      endDate: contract.endDate.split('T')[0],
      contractValue: contract.contractValue,
      description: contract.description,
      assignedProject: contract.assignedProject?._id || '',
      responsiblePerson: contract.responsiblePerson
    });
    setShowEditModal(true);
  };

  const openViewModal = (contract) => {
    setSelectedContract(contract);
    setShowViewModal(true);
  };

  const openFileModal = (contract) => {
    setSelectedContract(contract);
    setShowFileModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      case 'Completed': return 'bg-blue-100 text-blue-800';
      case 'Terminated': return 'bg-red-100 text-red-800';
      case 'Expired': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Active': return <CheckCircle size={16} />;
      case 'Pending': return <Clock size={16} />;
      case 'Completed': return <CheckCircle size={16} />;
      case 'Terminated': return <X size={16} />;
      case 'Expired': return <AlertCircle size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const getStatusTranslation = (status) => {
    switch (status) {
      case 'Active': return t('contracts.statusActive');
      case 'Pending': return t('contracts.statusPending');
      case 'Completed': return t('contracts.statusCompleted');
      case 'Terminated': return t('contracts.statusTerminated');
      case 'Expired': return t('contracts.statusExpired');
      default: return status;
    }
  };

  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = contract.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contract.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contract.contractId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || contract.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: 'BGN'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('bg-BG', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        <p className="ml-4 text-gray-600">{t('contracts.loading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center pt-16">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('contracts.contractsManagement')}</h1>
          <p className="text-gray-600 mt-2">{t('contracts.manageBusinessContracts')}</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
        >
          <Plus size={20} />
          {t('contracts.addNewContract')}
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('contracts')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'contracts'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <FileText size={16} />
              {t('contracts.contracts')}
            </div>
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Settings size={16} />
              {t('contracts.settings')}
            </div>
          </button>
        </nav>
      </div>

      {/* Contracts Tab */}
      {activeTab === 'contracts' && (
        <div className="space-y-6">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder={t('contracts.searchContracts')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">{t('contracts.allStatuses')}</option>
              <option value="Active">{t('contracts.statusActive')}</option>
              <option value="Pending">{t('contracts.statusPending')}</option>
              <option value="Completed">{t('contracts.statusCompleted')}</option>
              <option value="Terminated">{t('contracts.statusTerminated')}</option>
              <option value="Expired">{t('contracts.statusExpired')}</option>
            </select>
          </div>

          {/* Contracts Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('contracts.contractId')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('contracts.name')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('contracts.client')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('contracts.type')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('contracts.value')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('contracts.status')}
                    </th>
                                         <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                       {t('contracts.expiryDate')}
                     </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('contracts.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredContracts.map((contract) => (
                    <tr key={contract._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {contract.contractId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {contract.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {contract.clientName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {contract.contractType}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(contract.contractValue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(contract.status)}`}>
                          {getStatusIcon(contract.status)}
                          <span className="ml-1">{getStatusTranslation(contract.status)}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(contract.endDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openViewModal(contract)}
                            className="text-blue-600 hover:text-blue-900"
                            title={t('contracts.viewDetails')}
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => openEditModal(contract)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title={t('contracts.editContract')}
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => openFileModal(contract)}
                            className="text-green-600 hover:text-green-900"
                            title={t('contracts.manageFiles')}
                          >
                            <FileUp size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteContract(contract._id)}
                            className="text-red-600 hover:text-red-900"
                            title={t('contracts.deleteContract')}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredContracts.length === 0 && (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">{t('contracts.noContractsFound')}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || statusFilter !== 'all' 
                    ? t('contracts.tryAdjustingSearch')
                    : t('contracts.getStartedCreatingContract')
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('contracts.storageConfiguration')}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contracts.uploadDirectory')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customStoragePath}
                    onChange={(e) => setCustomStoragePath(e.target.value)}
                    placeholder={t('contracts.enterFullPathToStorage')}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button 
                    onClick={updateSettings}
                    disabled={savingSettings || !customStoragePath.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {savingSettings ? t('contracts.saving') : t('contracts.savePath')}
                  </button>
                  <button 
                    onClick={resetToDefault}
                    disabled={savingSettings}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:cursor-not-allowed"
                  >
                    {t('contracts.resetToDefault')}
                  </button>
                </div>
                {settingsMessage && (
                  <div className={`mt-2 p-2 rounded-md text-sm ${
                    settingsMessage.includes('Error') 
                      ? 'bg-red-100 text-red-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {settingsMessage}
                  </div>
                )}
                <p className="text-sm text-gray-500 mt-1">
                  {t('contracts.enterFullPathForContractFiles')}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {t('contracts.examplePaths')}
                </p>
                
                {/* Current Directory Display */}
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">{t('contracts.currentStorageDirectory')}:</h4>
                  <p className="text-sm text-blue-800 font-mono break-all">
                    {settings.uploadDirectory || t('contracts.defaultBackendDirectory')}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    {t('contracts.newFilesWillBeStoredHere')}
                  </p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contracts.maximumFileSize')}
                </label>
                <input
                  type="text"
                  value={`${(settings.maxFileSize || 0) / (1024 * 1024)} ${t('contracts.mb')}`}
                  readOnly
                  className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 w-32"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contracts.allowedFileTypes')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {(settings.allowedFileTypes || []).map((type, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('contracts.contractTemplates')}</h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.autoGenerateContractId || false}
                  readOnly
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  {t('contracts.autoGenerateContractId')}
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.requireProjectAssignment || false}
                  readOnly
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  {t('contracts.requireProjectAssignment')}
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={settings.sendExpiryNotifications || false}
                  readOnly
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  {t('contracts.sendExpiryNotifications')}
                </label>
              </div>
            </div>
          </div>

          {/* Contract Status Management */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('contracts.contractStatusManagement')}</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-3">
                  {t('contracts.automaticallyUpdateContractStatuses')}
                </p>
                <button
                  onClick={updateAllContractStatuses}
                  disabled={updatingStatuses}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {updatingStatuses ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {t('contracts.updating')}
                    </>
                  ) : (
                    <>
                      🔄 {t('contracts.updateAllContractStatuses')}
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  {t('contracts.statusUpdateFlow', { 
                    pending: t('contracts.statusPending'), 
                    active: t('contracts.statusActive'), 
                    expired: t('contracts.statusExpired') 
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Contract Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {showAddModal ? t('contracts.addNewContract') : t('contracts.editContract')}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts.contractName')}</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts.clientName')}</label>
                  <input
                    type="text"
                    required
                    value={formData.clientName}
                    onChange={(e) => setFormData({...formData, clientName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts.contractType')}</label>
                  <select
                    required
                    value={formData.contractType}
                    onChange={(e) => setFormData({...formData, contractType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Service">{t('contracts.service')}</option>
                    <option value="Supply">{t('contracts.supply')}</option>
                    <option value="Construction">{t('contracts.construction')}</option>
                    <option value="Consulting">{t('contracts.consulting')}</option>
                    <option value="Other">{t('contracts.other')}</option>
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts.startDate')}</label>
                    <input
                      type="date"
                      required
                      value={formData.startDate}
                      onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                                     <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts.expiryDate')}</label>
                     <input
                       type="date"
                       required
                       value={formData.endDate}
                       onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                     />
                   </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts.contractValue')}</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.contractValue}
                    onChange={(e) => setFormData({...formData, contractValue: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts.description')}</label>
                  <textarea
                    required
                    rows="3"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts.responsiblePerson')}</label>
                  <input
                    type="text"
                    required
                    value={formData.responsiblePerson}
                    onChange={(e) => setFormData({...formData, responsiblePerson: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setShowEditModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    {t('contracts.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {showAddModal ? t('contracts.createContract') : t('contracts.updateContract')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Contract Modal */}
      {showViewModal && selectedContract && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">{t('contracts.contractDetails')}</h3>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('contracts.contractId')}</label>
                    <p className="text-sm text-gray-900">{selectedContract.contractId}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('contracts.name')}</label>
                    <p className="text-sm text-gray-900">{selectedContract.name}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('contracts.client')}</label>
                    <p className="text-sm text-gray-900">{selectedContract.clientName}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('contracts.type')}</label>
                    <p className="text-sm text-gray-900">{selectedContract.contractType}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('contracts.status')}</label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedContract.status)}`}>
                      {getStatusIcon(selectedContract.status)}
                      <span className="ml-1">{getStatusTranslation(selectedContract.status)}</span>
                    </span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('contracts.startDate')}</label>
                    <p className="text-sm text-gray-900">{formatDate(selectedContract.startDate)}</p>
                  </div>
                  
                                     <div>
                     <label className="block text-sm font-medium text-gray-700">{t('contracts.expiryDate')}</label>
                     <p className="text-sm text-gray-900">{formatDate(selectedContract.endDate)}</p>
                   </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('contracts.value')}</label>
                    <p className="text-sm text-gray-900">{formatCurrency(selectedContract.contractValue)}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('contracts.responsiblePerson')}</label>
                    <p className="text-sm text-gray-900">{selectedContract.responsiblePerson}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('contracts.createdBy')}</label>
                    <p className="text-sm text-gray-900">{selectedContract.createdBy?.name || t('contracts.unknown')}</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('contracts.description')}</label>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">{selectedContract.description}</p>
              </div>
              
              {/* Files Section */}
              {selectedContract.files && selectedContract.files.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-md font-medium text-gray-900 mb-3">{t('contracts.attachedFiles')}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {selectedContract.files.map((file, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{file.originalName}</p>
                            <p className="text-xs text-gray-500">{file.category}</p>
                            <p className="text-xs text-gray-500">{(file.fileSize / 1024 / 1024).toFixed(2)} {t('contracts.mb')}</p>
                          </div>
                                                     <div className="flex space-x-2 ml-2">
                             <button
                               onClick={() => openFileLocation(selectedContract._id, file._id)}
                               className="text-blue-600 hover:text-blue-800"
                               title={t('contracts.openFileLocation')}
                             >
                               <FolderOpen size={16} />
                             </button>
                             <button
                               onClick={() => handleDeleteFile(selectedContract._id, file._id)}
                               className="text-red-600 hover:text-red-800"
                               title={t('contracts.deleteFile')}
                             >
                               <Trash2 size={16} />
                             </button>
                           </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  {t('contracts.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Upload Modal */}
      {showFileModal && selectedContract && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">{t('contracts.uploadFiles')}</h3>
                <button
                  onClick={() => setShowFileModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contracts.fileCategory')}</label>
                  <select
                    value={fileCategory}
                    onChange={(e) => setFileCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Main Contract">{t('contracts.mainContract')}</option>
                    <option value="Amendment">{t('contracts.amendment')}</option>
                    <option value="Attachment">{t('contracts.attachment')}</option>
                    <option value="Other">{t('contracts.other')}</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contracts.selectFiles')}</label>
                  <input
                    type="file"
                    multiple
                    onChange={(e) => setFiles(Array.from(e.target.files))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('contracts.maxFileSizeAndTypes')}
                  </p>
                </div>
                
                {files.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('contracts.selectedFiles')}</label>
                    <div className="space-y-2">
                      {files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm text-gray-700 truncate">{file.name}</span>
                          <button
                            onClick={() => setFiles(files.filter((_, i) => i !== index))}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setShowFileModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    {t('contracts.cancel')}
                  </button>
                  <button
                    onClick={handleFileUpload}
                    disabled={files.length === 0}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {t('contracts.uploadFiles')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contracts;
